import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createApiClient } from '@fls/api-client';
const AuthContext = createContext(null);
const TOKEN_KEY = 'vsp_access_token';
const REFRESH_TOKEN_KEY = 'vsp_refresh_token';
const normalizeIdentifier = (value) => value.includes('@') ? value : `${value}@local`;
export function AuthProvider({ children, apiBaseUrl }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const getAccessToken = useCallback(() => {
        return localStorage.getItem(TOKEN_KEY);
    }, []);
    const setTokens = useCallback((accessToken, refreshToken) => {
        localStorage.setItem(TOKEN_KEY, accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }, []);
    const clearTokens = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    }, []);
    const [apiClient] = useState(() => createApiClient({
        baseUrl: apiBaseUrl,
        getAccessToken,
        onTokenRefresh: ({ accessToken, refreshToken }) => {
            if (accessToken && refreshToken) {
                setTokens(accessToken, refreshToken);
            }
            else {
                clearTokens();
            }
        },
        onAuthError: () => {
            clearTokens();
            setUser(null);
        },
    }));
    // 초기 로드 시 사용자 정보 확인
    useEffect(() => {
        const initAuth = async () => {
            const token = getAccessToken();
            if (!token) {
                setIsLoading(false);
                return;
            }
            try {
                const currentUser = await apiClient.getCurrentUser();
                setUser(currentUser);
            }
            catch {
                clearTokens();
            }
            finally {
                setIsLoading(false);
            }
        };
        initAuth();
    }, [apiClient, getAccessToken, clearTokens]);
    const signIn = async (email, password) => {
        const response = await apiClient.signIn({
            email: normalizeIdentifier(email),
            password,
        });
        if (!response.accessToken || !response.refreshToken) {
            throw new Error('인증 토큰을 가져올 수 없습니다. 다시 시도해주세요.');
        }
        setTokens(response.accessToken, response.refreshToken);
        setUser(response.user);
    };
    const signUp = async (email, password, name) => {
        const response = await apiClient.signUp({
            email: normalizeIdentifier(email),
            password,
            name,
        });
        if (!response.accessToken || !response.refreshToken) {
            throw new Error('이메일 인증이 필요합니다. 받은 메일을 확인해주세요.');
        }
        setTokens(response.accessToken, response.refreshToken);
        setUser(response.user);
    };
    const signOut = async () => {
        try {
            await apiClient.signOut();
        }
        finally {
            clearTokens();
            setUser(null);
        }
    };
    return (<AuthContext.Provider value={{
            user,
            isLoading,
            isAuthenticated: !!user,
            signIn,
            signUp,
            signOut,
            apiClient,
        }}>
      {children}
    </AuthContext.Provider>);
}
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
